import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { DISCORD_URL } from '../constants/siteInfo';
import {
  TOPICS, MARKS, TOPIC_LABEL, TopicIcon, PostHeader, PostSections, TopicJump, formatDate, countItems,
} from '../components/changelog/changelogUi';

/**
 * /changelog — a bot frissítései, nyilvánosan (docs/changelog.md).
 *
 * A legutóbbi update TELJESEN kinyitva (képekkel), a korábbiak összecsukva:
 * témánként egy sor + "Read the full update". A szűrők (téma, jelölés) és a lap
 * az URL-ben élnek, így egy szűrt nézet is megosztható.
 */
export default function ChangelogPage() {
  const [params, setParams] = useSearchParams();
  const topic = TOPICS.some(t => t.id === params.get('topic')) ? params.get('topic') : '';
  const marksParam = params.get('marks');
  const marks = marksParam === null ? MARKS.map(m => m.id) : marksParam.split(',').filter(m => MARKS.some(x => x.id === m));
  const page = Math.max(1, parseInt(params.get('page'), 10) || 1);

  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  // A lap címe — távozáskor vissza: a többi oldal nem állít sajátot.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Changelog — MortisEngine';
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    let alive = true;
    const q = new URLSearchParams();
    if (topic) q.set('topic', topic);
    if (marksParam !== null) q.set('marks', marks.join(','));
    if (page > 1) q.set('page', String(page));
    setError('');
    api.get(`/changelog${q.toString() ? `?${q}` : ''}`)
      .then(res => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setError('The changelog could not be loaded. Try again in a moment.'); });
    return () => { alive = false; };
    // A `marks` a `marksParam`-ból következik.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, marksParam, page]);

  const setFilter = (next) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') p.delete(k); else p.set(k, v);
    }
    p.delete('page');
    setParams(p, { replace: true });
  };
  const toggleMark = (id) => {
    const set = new Set(marks);
    if (set.has(id)) set.delete(id); else set.add(id);
    const list = MARKS.map(m => m.id).filter(m => set.has(m));
    setFilter({ marks: list.length === MARKS.length ? null : list.join(',') });
  };
  const goPage = (n) => {
    const p = new URLSearchParams(params);
    if (n > 1) p.set('page', String(n)); else p.delete('page');
    setParams(p);
    window.scrollTo({ top: 0 });
  };

  const posts = data?.posts || [];
  const [first, ...rest] = posts;
  const filtered = !!topic || marksParam !== null;
  const rail = useMemo(() => (data?.index || []).slice(0, 8), [data]);

  return (
    <div>
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-7 flex flex-wrap items-end justify-between gap-6">
        <div className="flex flex-col gap-3.5 max-w-[720px]">
          <span className="eyebrow">Changelog</span>
          <h1 className="m-0 text-4xl sm:text-[44px] font-semibold leading-[1.08] tracking-[-0.025em] text-white">
            What changed in MortisEngine
          </h1>
          <p className="m-0 text-base leading-[1.6] text-gray-400">
            Every update to the bot, the loader and this site, newest first — with pictures where they help.
          </p>
        </div>
        <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary">Get updates on Discord ↗</a>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-[18px] flex flex-col gap-3">
        <div role="group" aria-label="Filter by topic" className="flex flex-wrap gap-2">
          <Chip on={!topic} onClick={() => setFilter({ topic: null })}>All topics</Chip>
          {TOPICS.map(t => (
            <Chip key={t.id} on={topic === t.id} onClick={() => setFilter({ topic: topic === t.id ? null : t.id })}>
              <TopicIcon topic={t.id} className="w-[15px] h-[15px]" />{t.label}
            </Chip>
          ))}
        </div>
        <div role="group" aria-label="Filter by kind" className="flex flex-wrap items-center gap-2">
          <span className="panel-label mr-1.5">Show</span>
          {MARKS.map(m => (
            <label key={m.id} className={`inline-flex items-center gap-2 h-[34px] px-3 rounded-lg border text-[13px] cursor-pointer
                                          transition-colors ${marks.includes(m.id)
                                            ? 'bg-gray-800 border-line-hover text-white'
                                            : 'border-[#30343a] text-gray-400 hover:border-line-hover'}`}>
              <input type="checkbox" className="checkbox accent-brand-500" checked={marks.includes(m.id)} onChange={() => toggleMark(m.id)} />
              {m.label}
            </label>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-[72px] flex flex-wrap gap-10 border-t border-line">
        <nav aria-label="Updates" className="hidden md:flex flex-[1_1_220px] max-w-[260px] flex-col gap-1 pt-6">
          <span className="panel-label px-3 pb-2">Updates</span>
          {rail.map(p => {
            const on = first && p.slug === first.slug && page === 1;
            return (
              <Link key={p.slug} to={`/changelog/${p.slug}`}
                    className={`flex flex-col gap-0.5 px-3 py-2.5 rounded-lg border transition-colors ${
                      on ? 'bg-gray-800 border-[#30343a]' : 'border-transparent hover:bg-[#181a1e]'}`}>
                <span className="font-mono text-xs text-gray-400">{formatDate(p.publishedAt)}</span>
                <span className={`text-sm leading-[1.35] ${on ? 'text-white' : 'text-gray-300'}`}>{p.title}</span>
              </Link>
            );
          })}
        </nav>

        <main className="flex-[999_1_640px] min-w-0 flex flex-col gap-7 pt-6">
          {error && <p className="text-sm text-danger">{error}</p>}
          {!data && !error && <ListSkeleton />}

          {data && posts.length === 0 && (
            <div className="card px-6 py-10 text-center">
              <p className="m-0 text-gray-300">{filtered ? 'Nothing matches these filters.' : 'No updates yet.'}</p>
              {filtered && (
                <button type="button" className="link mt-3 text-sm" onClick={() => setParams({}, { replace: true })}>
                  Clear the filters
                </button>
              )}
            </div>
          )}

          {first && page === 1 && (
            <article className="flex flex-col gap-[18px]">
              <PostHeader post={first} link />
              <TopicJump post={first} />
              <PostSections post={first} />
            </article>
          )}

          {(page === 1 ? rest : posts).length > 0 && (
            <>
              {page === 1 && (
                <div className="flex items-center gap-3.5 pt-3"><span className="panel-label">Earlier</span><span className="flex-1 h-px bg-line" /></div>
              )}
              {(page === 1 ? rest : posts).map(p => <OlderCard key={p.slug} post={p} />)}
            </>
          )}

          {data && data.pages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="font-mono text-[12.5px] text-gray-500">Page {data.page} of {data.pages}</span>
              <div className="flex gap-2">
                {data.page > 1 && <button type="button" className="btn-secondary" onClick={() => goPage(data.page - 1)}>← Newer updates</button>}
                {data.page < data.pages && <button type="button" className="btn-secondary" onClick={() => goPage(data.page + 1)}>Older updates →</button>}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function Chip({ on, onClick, children }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
            className={`inline-flex items-center gap-[7px] h-[34px] px-3 rounded-lg border text-[13px] whitespace-nowrap transition-colors ${
              on ? 'bg-gray-800 border-line-hover text-white' : 'border-[#30343a] text-gray-300 hover:border-line-hover hover:text-white'}`}>
      {children}
    </button>
  );
}

function OlderCard({ post }) {
  return (
    <article className="card px-7 py-6 flex flex-col gap-3">
      <span className="font-mono text-[13px] text-gray-400">
        {post.version ? `${post.version} · ` : ''}{formatDate(post.publishedAt)}
      </span>
      <h2 className="m-0 text-[22px] font-semibold tracking-[-0.015em]">
        <Link to={`/changelog/${post.slug}`} className="text-white hover:text-brand-200 transition-colors">{post.title}</Link>
      </h2>
      {post.summary && <p className="m-0 text-[15px] leading-[1.6] text-gray-300">{post.summary}</p>}
      <ul className="list-none m-0 mt-1 p-0">
        {post.sections.map((s, i) => {
          const n = countItems(s);
          return (
            <li key={i} className="flex items-center gap-3 py-[9px] border-t border-gray-800">
              <span className="text-gray-400 flex"><TopicIcon topic={s.topic} className="w-[15px] h-[15px]" /></span>
              <span className="w-[92px] flex-shrink-0 font-mono text-xs font-medium uppercase tracking-[0.1em] text-brand-500">
                {TOPIC_LABEL[s.topic]}
              </span>
              <span className="flex-1 min-w-0 text-[14.5px] text-gray-200 truncate">{s.title}</span>
              <span className="font-mono text-xs text-gray-500 whitespace-nowrap">{n} change{n === 1 ? '' : 's'}</span>
            </li>
          );
        })}
      </ul>
      <Link to={`/changelog/${post.slug}`} className="link self-start text-sm">Read the full update →</Link>
    </article>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-hidden="true">
      <div className="skeleton h-5 w-48" />
      <div className="skeleton h-9 w-3/4" />
      <div className="skeleton h-5 w-full" />
      <div className="skeleton h-40 w-full" />
    </div>
  );
}
